import moment from "moment";

import { api } from "services";
import { QuoteStatus } from "types/enums";
import { Quote } from "types/quotes";

import { addBusinessDays } from "./businessDays";

// Regra de liquidação: pagamento (link ou máquina física) hoje →
// repasse ao prestador em D+2 dias úteis (pagamento + split).
export const REPASSE_BUSINESS_DAYS = 2;

export function getRepasseDate(paymentDate: string): string {
  return addBusinessDays(paymentDate, REPASSE_BUSINESS_DAYS).format(
    "YYYY-MM-DD"
  );
}

// Datas de pagamento cujo repasse (D+2 úteis) cai exatamente em `repasseDay`.
// Pode ser mais de uma: sexta, sábado e domingo desembocam todos na terça.
export function paymentDatesForRepasseDay(repasseDay: string): string[] {
  const target = moment(repasseDay).format("YYYY-MM-DD");
  const result: string[] = [];
  // 10 dias de retrocesso cobrem fim de semana + feriados emendados
  for (let i = 1; i <= 10; i++) {
    const candidate = moment(repasseDay)
      .subtract(i, "days")
      .format("YYYY-MM-DD");
    if (getRepasseDate(candidate) === target) {
      result.push(candidate);
    }
  }
  return result;
}

export interface RepasseDayTotals {
  agendamento: number; // repasses previstos (status Em Agendamento)
  repassado: number; // já pagos/repassados
}

interface FetchTotalsOptions {
  startDate: string; // YYYY-MM-DD (inclusive)
  endDate: string; // YYYY-MM-DD (inclusive)
  companyId?: number;
  professionalId?: number;
  signal?: AbortSignal;
}

// Totais de repasse por dia. Usa a mesma previsão de pagamentos do
// calendário principal (/Payment/Forecasting) e desloca cada data em
// D+2 dias úteis — a data em que o split é creditado ao prestador.
export async function fetchRepasseTotals(
  opts: FetchTotalsOptions
): Promise<Record<string, RepasseDayTotals>> {
  const { data } = await api.post(
    "/Payment/Forecasting",
    {
      // Folga retroativa: pagamentos antes do início do mês visível
      // podem ter repasse dentro dele.
      startDate: moment(opts.startDate)
        .subtract(10, "days")
        .format("YYYY-MM-DD"),
      endDate: opts.endDate,
      companyId: opts.companyId === -1 ? undefined : opts.companyId,
      professionalId:
        opts.professionalId === -1 ? undefined : opts.professionalId,
    },
    { signal: opts.signal }
  );

  const byDay: Record<string, RepasseDayTotals> = {};
  for (const item of Array.isArray(data) ? data : []) {
    const paymentDate = String(item.date).substring(0, 10);
    const repasseDate = getRepasseDate(paymentDate);
    if (repasseDate < opts.startDate || repasseDate > opts.endDate) continue;

    byDay[repasseDate] = byDay[repasseDate] ?? {
      agendamento: 0,
      repassado: 0,
    };
    byDay[repasseDate].agendamento += item.totalForecastValue ?? 0;
    byDay[repasseDate].repassado += item.totalPaidValue ?? 0;
  }
  return byDay;
}

export interface RepasseQuote {
  quote: Quote;
  value: number; // valor do repasse (compensationValue)
  isPaidOut: boolean; // true = orçamento concluído (já repassado)
}

interface FetchDayOptions {
  date: string; // YYYY-MM-DD — dia do repasse
  companyId?: number;
  professionalId?: number;
  signal?: AbortSignal;
}

// Orçamentos cujo repasse cai no dia: consulta os detalhes de previsão
// de cada data de pagamento que desemboca (D+2 úteis) neste dia.
export async function fetchRepasseQuotesForDay(
  opts: FetchDayOptions
): Promise<RepasseQuote[]> {
  const paymentDates = paymentDatesForRepasseDay(opts.date);

  const responses = await Promise.all(
    paymentDates.map((date) =>
      api.get("/Payment/QuoteForecastingDetails", {
        params: {
          date,
          companyId: opts.companyId === -1 ? undefined : opts.companyId,
          professionalId:
            opts.professionalId === -1 ? undefined : opts.professionalId,
        },
        signal: opts.signal,
        timeout: 30000,
      })
    )
  );

  const quotes: Quote[] = responses.flatMap((r) =>
    Array.isArray(r.data) ? r.data : []
  );

  return quotes.map((q) => ({
    quote: q,
    value: q.payment?.compensationValue ?? 0,
    isPaidOut: q.quoteStatus === QuoteStatus.Finished,
  }));
}
